import React, { useState, useCallback, useMemo, useEffect } from 'react';
import cognitoAuthService from '../services/cognitoAuthService';
import './StunningCoralLogin.css';

const StunningCoralLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTooltip, setActiveTooltip] = useState(null);

  // Redirect if already logged in
  useEffect(() => {
    if (cognitoAuthService.isAuthenticated()) {
      cognitoAuthService.redirectToDashboard();
    }
  }, []);

  // Feature badges data - memoized for performance
  const features = useMemo(() => [
    {
      id: 1,
      icon: 'fas fa-graduation-cap',
      title: 'Smart Learning',
      description: 'AI-powered personalized learning paths with adaptive content delivery. Track student progress in real-time.'
    },
    {
      id: 2,
      icon: 'fas fa-chalkboard-teacher',
      title: 'Teacher Portal',
      description: 'Comprehensive tools for lesson planning, attendance tracking, grade management, and direct parent communication.'
    },
    {
      id: 3,
      icon: 'fas fa-user-graduate',
      title: 'Student Hub',
      description: 'Centralized access to assignments, grades, schedules, and learning resources with peer collaboration features.'
    },
    {
      id: 4,
      icon: 'fas fa-chart-line',
      title: 'Analytics',
      description: 'Advanced data insights with real-time dashboards, predictive analytics, and customizable reports.'
    },
    {
      id: 5,
      icon: 'fas fa-shield-alt',
      title: 'Secure Access',
      description: 'Enterprise-grade security with multi-factor authentication, role-based permissions, and encrypted data storage.'
    },
    {
      id: 6,
      icon: 'fas fa-cloud',
      title: 'Cloud Based',
      description: 'Access your school data anytime, anywhere with 99.9% uptime guarantee and automatic backups.'
    },
    {
      id: 7,
      icon: 'fas fa-comments',
      title: 'Communication Hub',
      description: 'Instant messaging connecting teachers, students, and parents with announcements and updates.'
    },
    {
      id: 8,
      icon: 'fas fa-bus',
      title: 'Transport Tracking',
      description: 'Real-time GPS tracking of school buses with live location updates and safety alerts.'
    }
  ], []);

  // Optimized login handler - no async/await inside setState
  const handleLogin = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!email || !password) {
        setError('Please enter both email and password');
        setLoading(false);
        return;
      }

      // Authenticate with Cognito
      const result = await cognitoAuthService.authenticateUser(email, password);

      if (result.success) {
        // Smooth transition to dashboard
        setTimeout(() => {
          cognitoAuthService.redirectToDashboard();
        }, 300);
      }
    } catch (err) {
      setError(err.message || 'Authentication failed. Please try again.');
      setLoading(false);
    }
  }, [email, password]);

  const handleBadgeClick = useCallback((featureId) => {
    setActiveTooltip(activeTooltip === featureId ? null : featureId);
  }, [activeTooltip]);

  // Close tooltips on outside click
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (!e.target.closest('.badge')) {
        setActiveTooltip(null);
      }
    };

    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #3B82F6 0%, #60A5FA 35%, #93C5FD 70%, #DBEAFE 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden', fontFamily: "'Inter', sans-serif" }}>
      {/* Background Orbs */}
      <div style={{ position: 'absolute', width: '100%', height: '100%', overflow: 'hidden' }}>
        <div className="orb orb-1"></div>
        <div className="orb orb-2"></div>
        <div className="orb orb-3"></div>
      </div>

      {/* Main Container */}
      <div className="login-container">
        {/* Left Panel - Features */}
        <div className="left-panel">
          <div className="logo-section">
            <img 
              src="/AstraVision_Logo.png" 
              alt="AstraVision Logo"
              style={{ width: '300px', height: 'auto' }}
            />
          </div>

          <h1 className="hero-title">
            Modern<br/>
            <span className="gradient-text">Education</span><br/>
            <span className="gradient-text">Platform</span>
          </h1>

          <p className="hero-subtitle">
            Empowering institutions with innovative technology for seamless management and enhanced learning experiences.
          </p>

          <div className="feature-badges">
            {features.map((feature) => (
              <button
                key={feature.id}
                className={`badge ${activeTooltip === feature.id ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleBadgeClick(feature.id);
                }}
                aria-label={feature.title}
              >
                <i className={feature.icon}></i>
                {feature.title}
                <div className="badge-tooltip">{feature.description}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Right Panel - Login Form */}
        <div className="right-panel">
          <div className="login-box">
            <div className="login-header">
              <h2 className="login-title">Welcome Back</h2>
              <p className="login-subtitle">Sign in to your school dashboard</p>
            </div>

            {error && (
              <div style={{
                marginBottom: '20px',
                padding: '12px 16px',
                background: '#FEE2E2',
                border: '1px solid #FCA5A5',
                borderRadius: '8px',
                color: '#DC2626',
                fontSize: '14px',
                fontWeight: '500',
                animation: 'slideDown 0.3s ease'
              }}>
                <i className="fas fa-exclamation-circle" style={{ marginRight: '8px' }}></i>
                {error}
              </div>
            )}

            <form onSubmit={handleLogin} style={{ width: '100%' }}>
              <div className="input-group">
                <label className="input-label">Email Address</label>
                <input
                  type="email"
                  className="input-field"
                  placeholder="admin@school.edu"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  autoComplete="email"
                />
              </div>

              <div className="input-group">
                <label className="input-label">Password</label>
                <input
                  type="password"
                  className="input-field"
                  placeholder="••••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  autoComplete="current-password"
                />
              </div>

              <button
                type="submit"
                className="signin-btn"
                disabled={loading}
                style={{
                  opacity: loading ? 0.8 : 1,
                  cursor: loading ? 'wait' : 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                    Signing in...
                  </>
                ) : (
                  'Sign In to Dashboard'
                )}
              </button>

              <p className="footer-text">
                <a href="https://absolutevision.in/support">Need help?</a> | 
                <a href="https://absolutevision.in/contact" style={{ marginLeft: '8px' }}>Contact Support</a>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StunningCoralLogin;
